import { useState, useEffect } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
// Import from the `/pure` entry so that importing this module does NOT inject
// the js.stripe.com <script> as a side effect. The default `@stripe/stripe-js`
// entry eagerly loads Stripe.js on import; combined with the un-code-split
// bundle that meant every page (incl. noindex PPC landing pages like
// /lp/leicestershire) loaded Stripe.js and reported "Failed to load Stripe.js"
// to Sentry whenever an ad/privacy blocker or flaky mobile network blocked the
// script — even though no one on those pages is checking out.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { PaymentIntentResult, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';
import { PaymentSuccess } from './payment-success';
import { api } from '@/lib/api';
import { trackPaymentFailure, trackPurchase } from '@/lib/analytics';
import { clsx } from 'clsx';
import { CouncilPackProof } from '@/components/claims/council-pack-proof';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Lazily load Stripe.js — only when checkout actually opens, and only once.
// The rejection is caught here so a blocked/failed script never becomes an
// unhandled promise rejection (which is what Sentry was capturing); instead it
// resolves to null and the modal shows a friendly, actionable message.
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  if (!stripePublishableKey) return Promise.resolve(null);
  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey).catch((err) => {
      // Reset so reopening checkout can retry the load.
      stripePromise = null;
      console.error('Stripe.js failed to load:', err);
      return null;
    });
  }
  return stripePromise;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  claimId: string;
  packType: 'council' | 'court';
}

const PACK_INFO = {
  council: {
    name: 'Council Claim Pack',
    price: 35,
    description: 'Professional claim letter, evidence bundle & submission guide',
    icon: '📋',
    features: [
      'Formal claim letter citing relevant law',
      'Organised evidence bundle',
      'Council-specific submission guide',
      'Know Your Rights briefing',
    ],
  },
  court: {
    name: 'Court Escalation Support',
    price: 0,
    description: 'Court document preparation is not currently available to purchase',
    icon: '⚖️',
    features: [
      'Guidance for reviewing a council rejection',
      'Escalation checklist',
      'Links to official small claims court resources',
      'Court document preparation planned for a later release',
    ],
  },
};

export function PaymentModal({
  isOpen,
  onClose,
  onSuccess,
  claimId,
  packType,
}: PaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  const packInfo = PACK_INFO[packType];

  // Don't allow dismissing the modal mid-charge (backdrop, Escape, X button)
  const handleClose = () => {
    if (isPaymentProcessing) return;
    onClose();
  };

  // Load Stripe.js + create the payment intent when the modal opens
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setStripe(null);
      setError(null);
      setShowSuccess(false);
      setIsPaymentProcessing(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      if (packType === 'court') {
        setError('Court document preparation is not currently available to purchase.');
        return;
      }

      if (!stripePublishableKey) {
        setError('Payments are temporarily unavailable. Please contact support.');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Load Stripe.js first — this is what fails when a browser ad/privacy
        // blocker or flaky connection blocks js.stripe.com.
        const stripeInstance = await getStripe();
        if (cancelled) return;
        if (!stripeInstance) {
          trackPaymentFailure(packType, 'stripe_load_failed');
          setError(
            "We couldn't load the secure payment form. This is usually caused by a browser ad or privacy blocker — please disable it for this site (or try another browser), then reopen checkout."
          );
          return;
        }
        setStripe(stripeInstance);

        const { clientSecret: secret } = await api.post<{ clientSecret: string }>(
          '/api/payments/create-payment-intent',
          {
            claimId,
            productType: packType === 'council' ? 'council_pack' : 'court_pack',
          }
        );
        if (cancelled) return;
        setClientSecret(secret);
      } catch (err) {
        if (cancelled) return;
        console.error('Payment intent error:', err);
        trackPaymentFailure(packType, 'payment_intent_failed');
        setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [isOpen, claimId, packType]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPaymentProcessing) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isPaymentProcessing]);

  if (!isOpen) return null;

  // Show success modal after payment
  if (showSuccess) {
    return (
      <PaymentSuccess
        isOpen={true}
        onClose={() => {
          setShowSuccess(false);
          onSuccess();
        }}
        packType={packType}
        claimId={claimId}
      />
    );
  }

  const elementsOptions: StripeElementsOptions = {
    clientSecret: clientSecret || undefined,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0f766e', // brand-700
        colorBackground: '#ffffff',
        colorText: '#0f172a', // brand-950
        colorDanger: '#dc2626',
        fontFamily: '"DM Sans", system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '12px',
        fontSizeBase: '15px',
      },
      rules: {
        '.Input': {
          border: '2px solid #e2e8f0',
          boxShadow: 'none',
          padding: '12px 14px',
        },
        '.Input:focus': {
          border: '2px solid #0f766e',
          boxShadow: '0 0 0 3px rgba(15, 118, 110, 0.1)',
        },
        '.Label': {
          fontWeight: '500',
          marginBottom: '6px',
        },
        '.Tab': {
          border: '2px solid #e2e8f0',
          borderRadius: '10px',
        },
        '.Tab--selected': {
          border: '2px solid #0f766e',
          backgroundColor: '#f0fdfa',
        },
      },
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-950/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-surface-100 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'w-10 h-10 rounded-xl flex items-center justify-center text-xl',
                packType === 'council' ? 'bg-brand-100' : 'bg-amber-100'
              )}>
                {packInfo.icon}
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-brand-950">
                  {packInfo.name}
                </h2>
                <p className="text-sm text-surface-600">
                  One-time payment
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isPaymentProcessing}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                isPaymentProcessing
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:bg-surface-100'
              )}
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Price Display - Enhanced with gradient background */}
          <div className={clsx(
            'relative text-center mb-6 py-6 -mx-6 px-6',
            packType === 'council' 
              ? 'bg-gradient-to-br from-brand-50 via-brand-50/50 to-accent-50/30'
              : 'bg-gradient-to-br from-amber-50 via-amber-50/50 to-orange-50/30'
          )}>
            {/* Decorative dots */}
            <div className="absolute top-3 left-6 flex gap-1.5">
              <div className={clsx('w-2 h-2 rounded-full', packType === 'council' ? 'bg-brand-300' : 'bg-amber-300')} />
              <div className={clsx('w-2 h-2 rounded-full', packType === 'council' ? 'bg-brand-200' : 'bg-amber-200')} />
              <div className={clsx('w-2 h-2 rounded-full', packType === 'council' ? 'bg-brand-100' : 'bg-amber-100')} />
            </div>
            
            <div className="inline-flex items-baseline gap-0.5">
              <span className="text-lg font-medium text-surface-500">£</span>
              <span className={clsx(
                'text-5xl font-display font-bold tracking-tight',
                packType === 'council' ? 'text-brand-950' : 'text-amber-900'
              )}>
                {packInfo.price}
              </span>
              <span className="text-lg text-surface-400">.00</span>
            </div>
            <p className="text-sm text-surface-600 mt-2 max-w-xs mx-auto">
              {packInfo.description}
            </p>
            
            {/* One-time payment badge */}
            <div className={clsx(
              'inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium',
              packType === 'council' 
                ? 'bg-brand-100/80 text-brand-700'
                : 'bg-amber-100/80 text-amber-700'
            )}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              One-time payment • No subscription
            </div>
            <p className="mx-auto mt-3 max-w-sm text-xs leading-relaxed text-surface-600">
              Personalised preparation begins after payment. Once it has begun, refund rights
              may be limited, subject to your statutory consumer rights and our terms.
            </p>
          </div>

          {/* What's included - Enhanced card design */}
          <div className={clsx(
            'rounded-xl p-4 mb-6 border',
            packType === 'council'
              ? 'bg-gradient-to-br from-white to-brand-50/30 border-brand-100'
              : 'bg-gradient-to-br from-white to-amber-50/30 border-amber-100'
          )}>
            <p className={clsx(
              'text-xs font-bold uppercase tracking-wider mb-3',
              packType === 'council' ? 'text-brand-600' : 'text-amber-600'
            )}>
              ✨ What's included
            </p>
            <ul className="space-y-2.5">
              {packInfo.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-brand-900">
                  <div className={clsx(
                    'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                    packType === 'council' ? 'bg-success-100' : 'bg-success-100'
                  )}>
                    <svg className="w-3 h-3 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="font-medium">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Payment Form */}
          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-xl">
              <p className="text-sm text-error-700">{error}</p>
            </div>
          )}

          {isLoading && !clientSecret && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin h-8 w-8 text-brand-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="text-sm text-surface-600">Preparing secure checkout...</p>
              </div>
            </div>
          )}

          {stripe && clientSecret && (
            <Elements stripe={stripe} options={elementsOptions}>
              <CheckoutForm
                packType={packType}
                price={packInfo.price}
                onSuccess={() => setShowSuccess(true)}
                onCancel={handleClose}
                onProcessingChange={setIsPaymentProcessing}
              />
            </Elements>
          )}
        </div>

        {/* Security Badge - Enhanced trust indicators */}
        <div className="px-6 pb-6">
          <div className="flex flex-col items-center gap-3">
            {/* Trust badges row */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs text-surface-600">
                <svg className="w-4 h-4 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Secure checkout</span>
              </div>
              <div className="w-px h-3 bg-surface-200" />
              <div className="flex items-center gap-1.5 text-xs text-surface-600">
                <svg className="w-4 h-4 text-surface-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>256-bit SSL</span>
              </div>
              <div className="w-px h-3 bg-surface-200" />
              <div className="flex items-center gap-1.5 text-xs text-surface-600">
                <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span>All cards accepted</span>
              </div>
            </div>
            
            {/* Stripe powered badge */}
            <div className="flex items-center gap-2 text-xs text-surface-400">
              <span>Powered by</span>
              <svg className="h-5 w-auto" viewBox="0 0 60 25" fill="currentColor" aria-label="Stripe">
                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.48zm-3.67-3c0-1.3-.73-2.7-2.26-2.7-1.46 0-2.38 1.38-2.49 2.7h4.75zM41.4 5.5v14.6h4.14V5.5H41.4zm0-4.36v3.1h4.14V1.14H41.4zM38.63 5.5h-3.72v-.9c0-.86.37-1.32 1.21-1.32.86 0 1.71.28 1.71.28V.62S35.8 0 34.28 0c-2.53 0-3.88 1.66-3.88 4.3v1.2h-2.53v3.46h2.53v11.14h4.14V8.96h2.46l.63-3.46zm-12.6 14.6h4.14V5.5h-4.14v14.6zm0-18.96v3.1h4.14V1.14h-4.14zM16.16 5.5c-.83 0-1.6.48-1.94 1.24l-.15.36V5.5H10v14.6h4.1v-7.2c0-2.2.96-3.22 2.8-3.22.5 0 1.02.1 1.44.26V5.77a4.27 4.27 0 0 0-2.18-.27zm-8.87 0h-4v14.6h4.14v-8.1c0-1.52.6-2.32 1.9-2.32.47 0 .97.1 1.37.22V5.66a4.09 4.09 0 0 0-1.44-.16c-1.1 0-1.57.43-1.97 1.34V5.5z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Inner checkout form component
export function CheckoutForm({
  packType,
  price,
  onSuccess,
  onCancel,
  onProcessingChange,
}: {
  packType: 'council' | 'court';
  price: number;
  onSuccess: () => void;
  onCancel: () => void;
  onProcessingChange: (processing: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once Stripe has taken the payment but the backend /confirm call hasn't succeeded yet
  const [pendingIntentId, setPendingIntentId] = useState<string | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<'idle' | 'confirming' | 'failed'>('idle');

  // Keep the parent modal informed so it can block closing mid-charge
  useEffect(() => {
    onProcessingChange(isProcessing);
  }, [isProcessing, onProcessingChange]);

  // Confirm the payment with the backend, with a few automatic retries (backoff)
  const confirmWithBackend = async (intentId: string): Promise<boolean> => {
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** (attempt - 1)));
      }
      try {
        await api.post('/api/payments/confirm', { paymentIntentId: intentId });
        return true;
      } catch (err) {
        console.error('Error confirming payment:', err);
      }
    }
    return false;
  };

  const handleConfirm = async (intentId: string) => {
    setConfirmStatus('confirming');
    setIsProcessing(true);
    const confirmed = await confirmWithBackend(intentId);
    setIsProcessing(false);
    if (confirmed) {
      setConfirmStatus('idle');
      setPendingIntentId(null);
      trackPurchase(packType, price, intentId);
      onSuccess();
    } else {
      setConfirmStatus('failed');
      trackPaymentFailure(packType, 'backend_confirmation_failed');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      let submitResult: Awaited<ReturnType<typeof elements.submit>>;
      try {
        submitResult = await elements.submit();
      } catch {
        trackPaymentFailure(packType, 'payment_element_submit_failed');
        setError("We couldn't validate the payment form. Please try again.");
        return;
      }
      if (submitResult.error) {
        trackPaymentFailure(packType, 'payment_element_submit_failed');
        setError(submitResult.error.message || 'Payment failed');
        return;
      }

      let confirmResult: PaymentIntentResult;
      try {
        confirmResult = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });
      } catch {
        trackPaymentFailure(packType, 'payment_confirmation_failed');
        setError("We couldn't confirm the payment. Please try again.");
        return;
      }

      if (confirmResult.error) {
        trackPaymentFailure(packType, 'payment_confirmation_failed');
        setError(confirmResult.error.message || 'Payment failed');
        return;
      }

      // Payment succeeded - now confirm with backend and trigger pack generation
      if (confirmResult.paymentIntent?.id) {
        setPendingIntentId(confirmResult.paymentIntent.id);
        await handleConfirm(confirmResult.paymentIntent.id);
      } else {
        trackPaymentFailure(packType, 'missing_payment_intent');
        setError('Payment status could not be verified. Please contact support before retrying.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Payment taken but backend confirmation pending/failed
  if (confirmStatus !== 'idle') {
    return (
      <div className="space-y-4">
        {confirmStatus === 'confirming' ? (
          <div className="p-4 bg-brand-50 border border-brand-100 rounded-xl">
            <div className="flex items-center gap-3">
              <svg className="animate-spin h-5 w-5 text-brand-600 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-brand-900">
                  Payment received — confirming your order…
                </p>
                <p className="text-xs text-brand-700 mt-0.5">
                  Please don't close this window.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 bg-success-50 border border-success-200 rounded-xl">
              <p className="text-sm font-medium text-success-800">
                ✓ Your payment was received.
              </p>
              <p className="text-sm text-success-700 mt-1">
                We couldn't confirm your order just now, but don't worry — your pack
                will be generated shortly. Check back in a few minutes or contact
                support if it doesn't appear.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={() => pendingIntentId && handleConfirm(pendingIntentId)}
                className="flex-1"
              >
                Retry confirmation
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CouncilPackProof compact />
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />

      {error && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-xl">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          loading={isProcessing}
          className={clsx(
            'flex-1',
            packType === 'court' && 'bg-amber-600 hover:bg-amber-700'
          )}
        >
          {isProcessing ? 'Processing...' : `Pay £${price}`}
        </Button>
      </div>
    </form>
  );
}
